import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, TrendingDown, DollarSign, Wallet, PieChart as PieIcon, Building2, Package } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend
} from "recharts";

const currency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const currency2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_LABEL = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function PnlPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/financials/pnl?year=${year}`).then(r => { setData(r.data); setLoading(false); });
  }, [year]);

  const monthlyChartData = useMemo(() =>
    (data?.monthly || []).map((m, i) => ({
      label: MONTH_LABEL[i],
      Income: m.income,
      Expenses: m.expenses,
      Net: m.net,
    })),
  [data]);

  const yoyData = useMemo(() => {
    if (!data) return [];
    return [
      { label: `${data.prev_year}`, Income: data.prev_year_totals.income, Expenses: data.prev_year_totals.expenses, Net: data.prev_year_totals.net },
      { label: `${data.year}`, Income: data.totals.income, Expenses: data.totals.expenses, Net: data.totals.net },
    ];
  }, [data]);

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  const downloadBundle = async () => {
    try {
      toast.info("Preparing your year-end bundle…");
      const res = await api.get("/reports/year-end-bundle", { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rojasproperty-yearend-${year}-bundle.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Year-end ${year} bundle downloaded`);
    } catch { toast.error("Bundle download failed"); }
  };

  return (
    <AppLayout
      title="Profit & Loss"
      subtitle="Income minus expenses — monthly, per-property & year-over-year"
      actions={
        <div className="flex gap-2 items-center">
          <Button data-testid="year-end-bundle-button" onClick={downloadBundle} variant="outline" className="gap-2" disabled={!data}>
            <Package className="h-4 w-4" /> Year-End Bundle
          </Button>
          <ExportMenu
            endpoint="/financials/pnl/export"
            filenameBase={`rojasproperty-pnl-${year}`}
            extraParams={{ year }}
            testidPrefix="export-pnl"
            disabled={!data}
          />
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="pnl-year-select" className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      }
    >
      {loading || !data ? (
        <div className="py-20 text-center text-slate-400 text-sm">Loading…</div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-testid="pnl-kpis">
            <KPI icon={Wallet} label="Total Income" value={currency(data.totals.income)} yoy={data.yoy.income_pct} hint={`paid rents ${year}`} tone="emerald" testid="kpi-income" />
            <KPI icon={DollarSign} label="Total Expenses" value={currency(data.totals.expenses)} yoy={data.yoy.expenses_pct} hint={`recorded ${year}`} tone="rose" invertYoy testid="kpi-expenses" />
            <KPI icon={TrendingUp} label="Net Profit" value={currency(data.totals.net)} yoy={data.yoy.net_pct} hint={data.totals.net >= 0 ? "in the green" : "in the red"} tone={data.totals.net >= 0 ? "emerald" : "rose"} testid="kpi-net" />
            <KPI icon={PieIcon} label="Profit Margin" value={`${data.totals.margin_pct}%`} hint="of income retained" tone="slate" testid="kpi-margin" />
          </div>

          {/* Monthly chart */}
          <Card className="p-6 border-slate-200 mb-6" data-testid="monthly-chart">
            <h3 className="font-display font-semibold mb-4">Monthly income vs expenses</h3>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={monthlyChartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                  <Tooltip formatter={(v) => currency2(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                  <Bar dataKey="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* YoY chart + Per-property */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-6 border-slate-200" data-testid="yoy-chart">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold">Year over year</h3>
                <span className="text-xs text-slate-500 font-mono-num">{data.prev_year} → {data.year}</span>
              </div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={yoyData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                    <Tooltip formatter={(v) => currency2(v)} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                    <Line type="monotone" dataKey="Income" stroke="#10b981" strokeWidth={2.5} dot={{ r: 5 }} />
                    <Line type="monotone" dataKey="Expenses" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 5 }} />
                    <Line type="monotone" dataKey="Net" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 border-slate-200" data-testid="top-categories-pnl">
              <h3 className="font-display font-semibold mb-4">Spend by category — {year}</h3>
              {data.by_category.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-12">No expenses recorded for {year}</div>
              ) : (
                <div className="space-y-2.5">
                  {data.by_category.slice(0, 8).map(c => {
                    const pct = data.totals.expenses ? (c.amount / data.totals.expenses) * 100 : 0;
                    return (
                      <div key={c.category}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-700 font-medium truncate">{c.category}</span>
                          <span className="font-mono-num text-slate-600">{currency2(c.amount)} <span className="text-slate-400">· {pct.toFixed(1)}%</span></span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-rose-500 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* Per-property */}
          <Card className="border-slate-200" data-testid="per-property-table">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-500" />
                <h3 className="font-display font-semibold">Per-property breakdown</h3>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Income, expenses & net contribution per unit. Sorted by net profit (highest first).</p>
            </div>
            {data.by_property.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">No property activity recorded for {year}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead className="text-right">Income</TableHead>
                    <TableHead className="text-right">Expenses</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_property.map(p => (
                    <TableRow key={p.property_id} data-testid={`pnl-property-${p.property_id}`}>
                      <TableCell className="font-medium">{p.property_name}</TableCell>
                      <TableCell className="text-right font-mono-num text-emerald-700">{currency2(p.income)}</TableCell>
                      <TableCell className="text-right font-mono-num text-rose-700">{currency2(p.expenses)}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${p.net >= 0 ? "text-slate-900" : "text-rose-700"}`}>{currency2(p.net)}</TableCell>
                      <TableCell className="text-right">
                        {p.income > 0 ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.margin >= 30 ? "bg-emerald-100 text-emerald-700" : p.margin >= 0 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                            {p.margin}%
                          </span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </>
      )}
    </AppLayout>
  );
}

function KPI({ icon: Icon, label, value, hint, yoy, tone = "slate", invertYoy = false, testid }) {
  const toneMap = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
  };
  const showYoy = yoy != null;
  // If invertYoy (e.g. expenses), going UP is bad
  const positive = invertYoy ? yoy < 0 : yoy > 0;
  return (
    <Card data-testid={testid} className="p-6 border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
          <div className="font-display text-2xl font-semibold mt-3 font-mono-num truncate">{value}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
            <span>{hint}</span>
            {showYoy && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium font-mono-num ${positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(yoy)}%
              </span>
            )}
          </div>
        </div>
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}
